import { useEffect, useState } from 'react';

import { Box, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectCurrentEntity,
    selectCurrentTenantId,
    selectNameInConflict,
    selectOpen,
    setCurrentEntity,
    setNameInConflict,
    setSaveButtonEnabled
} from 'common/components/EditDrawer/editDrawerSlice';
import { ErrorHandler } from 'common/components/errorHandler/ErrorHandler';
import { PageInfo, selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { getOptionLabelDefaultCallback,isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { WarningMessageWithCheckboxAcknowledgement } from 'common/components/sharedCustomComponents/WarningMessageWithCheckboxAcknowledgement/WarningMessageWithCheckboxAcknowledgement';
import { IDENTITY_RETRIEVAL_ERROR_MESSAGE, IDENTITY_RETRIEVAL_ERROR_TOOLTIP_MESSAGE, INACTIVE_IDENTITY_CHECKBOX_LABEL, INACTIVE_IDENTITY_WARNING_MESSAGE } from 'common/constants/inactiveIdentityCheckDisplayMessages';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserGroups } from 'common/services/api/UserGroups/UserGroups.thunk';
import { postUserGroupUserMembership } from 'common/services/api/UserGroupUserMembership/UserGroupUserMembership.thunk';
import { fetchUserProfilesById } from 'common/services/api/UserProfiles/UserProfiles.thunk';
import { postUserProfileUserGroup } from 'common/services/api/UserProfileUserGroups/UserProfileUserGroups.thunk';
import { UserDetails, UserGroup } from 'common/types/Entities';
import {
    DrawerEntitiesNames,
    EditDrawerContentProps,
    Operations
} from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { handleEditDrawerError } from 'common/utils/errorHandlers';
import { getUserProfileTenantUpdatePayload, updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

interface IConstructDrawerContext {
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
}

const USER_GROUP_MEMBERSHIP_POST_SUCCESS_MESSAGE = 'The User Group was successfully linked.';
const EXISTING_USER_GROUP_USER_LINKING_ERROR_MESSAGE = 'This User Group is already linked to this User.';

const constructCreateContentComponent = (
    context: IConstructDrawerContext,
    pageInfo: PageInfo | undefined,
    saveButtonFunction: () => void,
    userGroup: UserGroup | null,
    handleUserGroupChange: (e: unknown, userGroup: UserGroup | null) => void,
    inactiveIdentity: boolean,
    inactiveIdentityChecked: boolean,
    setInactiveIdentityChecked: (checked: boolean) => void,
    fetchingIdentityStatus: boolean,
    warningMessage: string,
    tooltipDescription?: string
) => {
    const tenantId = pageInfo?.id ?? '';
    const userFirstName = pageInfo?.firstName ? pageInfo.firstName : 'Unknown';
    const userLastName = pageInfo?.lastName ? pageInfo.lastName : 'User';
    const userFullName = `${userFirstName} ${userLastName}`;
    const tenantName = pageInfo?.name ?? '';
    const userGroupDescription = userGroup?.description ?? '';

    return (
        <EditDrawer
            title={context.entityName}
            saveButtonFunction={saveButtonFunction}
            processing={context.processing}
        >
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{ id: '', name: '', description: '', tenantId: '' }}
                    value={userGroup}
                    onChange={handleUserGroupChange}
                    label='Tenant User Group Search'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchUserGroups}
                    asyncThunkArgs={{ tenantId }}
                    displayMenuItemContent={(option: UserGroup) => <>{option.name}</>}
                    hasError={context.nameInConflict}
                    effectDependecyArray={[tenantId]}
                />
                {context.nameInConflict &&
                    <ErrorHandler
                        errorMessage={EXISTING_USER_GROUP_USER_LINKING_ERROR_MESSAGE}
                    />
                }
                {fetchingIdentityStatus &&
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '1rem' }}>
                        <CircularProgress size={20} data-testid='loading'/>
                        <Typography variant="body2" color="text.secondary">
                            Checking user identity statuses...
                        </Typography>
                    </Box>
                }
                <WarningMessageWithCheckboxAcknowledgement
                    show={!fetchingIdentityStatus && inactiveIdentity}
                    checked={inactiveIdentityChecked}
                    warningMessage={warningMessage}
                    checkboxLabel={INACTIVE_IDENTITY_CHECKBOX_LABEL}
                    tooltipDescription={tooltipDescription}
                    onCheckedChange={setInactiveIdentityChecked}
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="User Group Description"
                    variant="filled"
                    value={userGroupDescription}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Name"
                    variant="filled"
                    defaultValue={userFullName}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={tenantName}
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
};

const constructUserDetailsCreateContentComponent = (
    context: IConstructDrawerContext,
    saveButtonFunction: () => void,
    userGroup: UserGroup | null,
    handleUserGroupChange: (e: unknown, userGroup: UserGroup | null) => void,
    tenant: IUserProfileTenant | undefined,
    userDetails: UserDetails | null
) => {
    const tenantId = tenant?.id ?? '';
    const userFirstName = userDetails?.firstName ? userDetails.firstName : 'Unknown';
    const userLastName = userDetails?.lastName ? userDetails.lastName : 'User';
    const userFullName = `${userFirstName} ${userLastName}`;
    const tenantName = tenant?.name ?? '';
    const userGroupDescription = userGroup?.description ?? '';

    return (
        <EditDrawer
            title={context.entityName}
            saveButtonFunction={saveButtonFunction}
            processing={context.processing}
        >
            <Stack spacing={2}>
                <EntitiesSearchableCommonDropDown
                    initialEntityOption={{ id: '', name: '', description: '', tenantId: '' }}
                    value={userGroup}
                    onChange={handleUserGroupChange}
                    label='Tenant User Group Search'
                    getOptionLabel={getOptionLabelDefaultCallback}
                    isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                    asyncThunkAction={fetchUserGroups}
                    asyncThunkArgs={{ tenantId }}
                    displayMenuItemContent={(option: UserGroup) => <>{option.name}</>}
                    hasError={context.nameInConflict}
                    effectDependecyArray={[tenantId]}
                />
                {context.nameInConflict &&
                    <ErrorHandler
                        errorMessage={EXISTING_USER_GROUP_USER_LINKING_ERROR_MESSAGE}
                    />
                }
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="User Group Description"
                    variant="filled"
                    value={userGroupDescription}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Name"
                    variant="filled"
                    defaultValue={userFullName}
                    disabled
                />
                <TextField
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: '4px',
                    }}
                    size="small"
                    label="Tenant Name"
                    variant="filled"
                    defaultValue={tenantName}
                    disabled
                />
            </Stack>
        </EditDrawer>
    );
};

export function UserGroupUserMembershipCreateContent({
    title,
    tenant,
}: Readonly<EditDrawerContentProps<{ name: string }>>) {
    const [userGroup, setUserGroup] = useState<UserGroup | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const [inactiveIdentity, setInactiveIdentity] = useState<boolean>(false);
    const [inactiveIdentityChecked, setInactiveIdentityChecked] = useState<boolean>(false);
    const [fetchingIdentityStatus, setFetchingIdentityStatus] = useState<boolean>(false);
    const [warningMessage, setWarningMessage] = useState<string>(INACTIVE_IDENTITY_WARNING_MESSAGE);
    const [tooltipDescription, setTooltipDescription] = useState<string | undefined>(undefined);

    const nameInConflict = useAppSelector(selectNameInConflict);
    const pageInfo = useAppSelector(selectPageInfo);
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const { enqueueSnackbar } = useSnackbar();
    const currentEntity = useAppSelector(selectCurrentEntity);
    const currentTenantId = useAppSelector(selectCurrentTenantId);
    const userDetails = useAppSelector(selectUserDetails);
    const dispatch = useAppDispatch();

    useEffect(() => {
        if(currentId === DrawerIDs.UserGroup){
            setUserGroup(null);
            setInactiveIdentity(false);
            setInactiveIdentityChecked(false);
            setFetchingIdentityStatus(false);
            setWarningMessage(INACTIVE_IDENTITY_WARNING_MESSAGE);
            setTooltipDescription(undefined);
        }
    }, [drawerOpen, currentId]);

    // Fetch user's active identity status (only for AppSuiteUserGroup entity)
    useEffect(() => {
        if (pageInfo?.userId && drawerOpen && currentEntity === DrawerEntitiesNames.AppSuiteUserGroup) {
            setInactiveIdentity(false);
            setInactiveIdentityChecked(false);
            setFetchingIdentityStatus(true);
            dispatch(fetchUserProfilesById(pageInfo?.userId))
                .unwrap()
                .then((userProfiles) => {
                    const hasActiveIdentity = userProfiles?.some(
                        profile => profile.activeIdentityStatus === true
                    ) ?? false;
                    setInactiveIdentity(!hasActiveIdentity);
                    if (hasActiveIdentity) {
                        setInactiveIdentityChecked(false);
                    }
                    setWarningMessage(INACTIVE_IDENTITY_WARNING_MESSAGE);
                    setTooltipDescription(undefined);
                })
                .catch(() => {
                    // If API call fails, show error message and require acknowledgment
                    setInactiveIdentity(true);
                    setInactiveIdentityChecked(false);
                    setWarningMessage(IDENTITY_RETRIEVAL_ERROR_MESSAGE);
                    setTooltipDescription(IDENTITY_RETRIEVAL_ERROR_TOOLTIP_MESSAGE);
                })
                .finally(() => {
                    setFetchingIdentityStatus(false);
                });
        }
    }, [drawerOpen, pageInfo?.userId, currentEntity, dispatch]);

    const handleUserGroupChange = (_e: unknown, newUserGroup: UserGroup | null) => {
        setUserGroup(newUserGroup);
        dispatch(setSaveButtonEnabled(!!newUserGroup?.id));
        dispatch(setNameInConflict(false));
    };

    useEffect(() => {
        if (fetchingIdentityStatus) {
            dispatch(setSaveButtonEnabled(false));
        } else if (userGroup?.id && inactiveIdentity && !inactiveIdentityChecked) {
            dispatch(setSaveButtonEnabled(false));
        } else if (userGroup?.id) {
            dispatch(setSaveButtonEnabled(true));
        }
    }, [userGroup?.id, inactiveIdentity, inactiveIdentityChecked, fetchingIdentityStatus, dispatch]);

    const userGroupUserMembershipPostCallback = () => {
        setProcessing(true);
        const userGroupUserMembershipPayload = {
            tenantId: pageInfo?.id ?? '',
            userGroupId: userGroup?.id ?? '',
            userId: pageInfo?.userId ?? ''
        };
        dispatch(postUserGroupUserMembership(userGroupUserMembershipPayload))
            .unwrap()
            .then((result) => {
                enqueueSnackbar(USER_GROUP_MEMBERSHIP_POST_SUCCESS_MESSAGE, { variant: 'success' });
                dispatch(setRows(result.rows));
                dispatch(setNameInConflict(false));
                dispatch(setCurrentEntity());
                dispatch(closeDrawer());
            })
            .catch((error) => {
                handleEditDrawerError(
                    Operations.Link,
                    extractStatusCode(error.message),
                    'User Group',
                    dispatch,
                    enqueueSnackbar
                );
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    const userProfileUserGroupUserMembershipPostCallback = () => {
        setProcessing(true);
        const userProfileUserGroupUserMembershipPayload = {
            tenantId: tenant?.id ?? '',
            userGroupId: userGroup?.id ?? '',
            userId: userDetails?.userId ?? ''
        };
        dispatch(postUserProfileUserGroup(userProfileUserGroupUserMembershipPayload))
            .unwrap()
            .then(() => {
                enqueueSnackbar(USER_GROUP_MEMBERSHIP_POST_SUCCESS_MESSAGE, { variant: 'success' });
                const updateUserProfileTenantPayload = getUserProfileTenantUpdatePayload(tenant, userDetails);
                updateUserProfileTenant(updateUserProfileTenantPayload);
                dispatch(setNameInConflict(false));
                dispatch(setCurrentEntity());
                dispatch(closeDrawer());
            })
            .catch((error) => {
                handleEditDrawerError(
                    Operations.Link,
                    extractStatusCode(error.message),
                    'User Group',
                    dispatch,
                    enqueueSnackbar
                );
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    const context = {
        entityName: title,
        processing,
        nameInConflict: nameInConflict ?? false
    };

    switch(currentEntity) {
        case DrawerEntitiesNames.AppSuiteUserGroup: {
            return constructCreateContentComponent(
                context,
                pageInfo,
                userGroupUserMembershipPostCallback,
                userGroup,
                handleUserGroupChange,
                inactiveIdentity,
                inactiveIdentityChecked,
                setInactiveIdentityChecked,
                fetchingIdentityStatus,
                warningMessage,
                tooltipDescription
            );
        }
        case DrawerEntitiesNames.UserProfileUserGroup: {
            if (currentTenantId !== tenant?.id) {
                return null;
            }
            return constructUserDetailsCreateContentComponent(
                context,
                userProfileUserGroupUserMembershipPostCallback,
                userGroup,
                handleUserGroupChange,
                tenant,
                userDetails
            );
        }
        default: { return null; }
    }
}
